import { createContext, useCallback, useContext } from 'react';

import { Logger } from '@suntex.core/logging';

/**
 * When `enabled`, the homepage should redirect to `VITE_HOMEPAGE_REDIRECT_URL`.
 * Starts `true` and is set to `false` by `JwtUrlLogin` when the page was
 * loaded via a `?jwt=` URL parameter, so the user is not redirected away
 * during the JWT sign-in flow. The consuming Login component re-enables
 * it once post-login navigation completes, so subsequent visits to the
 * home route (e.g. after logout) redirect normally again.
 */

const logger = Logger.for('useHomepageRedirect');

export interface HomepageRedirectContext {
  redirectUrl: string | undefined;
  redirectEnabled: boolean;
  setRedirectEnabled: (enabled: boolean) => void;
}

export const HomepageRedirectContext = createContext<
  HomepageRedirectContext | undefined
>(undefined);

export function useHomepageRedirect(): Pick<
  HomepageRedirectContext,
  'redirectUrl' | 'setRedirectEnabled'
> {
  const context = useContext(HomepageRedirectContext);
  if (!context) {
    throw new Error(
      'useHomepageRedirect must be used within a HomepageRedirectProvider',
    );
  }

  const { redirectUrl, redirectEnabled, setRedirectEnabled } = context;
  const _setRedirectEnabled = useCallback(
    (enabled: boolean) => {
      logger.debug(`setRedirectEnabled(${enabled})`);
      setRedirectEnabled(enabled);
    },
    [setRedirectEnabled],
  );

  return {
    redirectUrl: redirectEnabled ? redirectUrl : undefined,
    setRedirectEnabled: _setRedirectEnabled,
  };
}
